<template>
    <MainComponent
        :id="anchorsPageProfile.recommendationsSection"
        ref="recommendationSection"
        :class="{'edit-mode': isEditionEnabled}"
        data-testid="recommendation-sections"
    >
        <template #title>
            <VJoyWrapper>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <h2 v-html="title"></h2>
            </VJoyWrapper>
        </template>

        <template #title-action>
            <VJoyWrapper align="flex-end" column-gap="1" no-margin row-gap="1">
                <ClientOnly v-if="isEditionEnabled">
                    <VJoyIconButton
                        :label="$t('general.edit')"
                        class="edit-button"
                        data-testid="open-recommendation-request"
                        icon="pen"
                        size="small"
                        variant="ghost"
                        @click="openEditRecommendation"
                    />
                    <template #fallback>
                        <SectionActionSkeleton />
                    </template>
                </ClientOnly>
                <LazyRecommendationsExternalTopActions v-if="isExternalActionsVisible" />
            </VJoyWrapper>
        </template>
        <template #content>
            <RecommendationsCount
                v-if="profile && hasFilteredRecommendations && recommendations?.recommendationsCount"
                :accepted-recommendations="recommendationsAccepted"
                :profile-firstname="profile?.firstname"
                :recommendations-count="recommendations?.recommendationsCount"
            />

            <LazyRecommendationsEditionList v-if="isEditionEnabled" />
            <LazyRecommendationsList v-else :recommendations="displayedRecommendations" />

            <ShowMoreByItemCount
                :label="t('profile.section.recommendation.show.all.label.v2', {count: displayedRecommendationsCount})"
                :limit="isEditionEnabled ? displayedRecommendationsCount : RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT"
                :total="displayedRecommendationsCount"
                test-id="read-more-recommendations"
                :locked="{
                    allowHref: true,
                    label: t('profile.section.recommendation.private', {0: profile?.firstname}),
                    componentName: 'profile-recommendations-section',
                    targetId: anchorsPageProfile.recommendationsSection,
                }"
                @collapse="toggleShowMoreRecommendations(false)"
                @expand="toggleShowMoreRecommendations(true)"
            />
        </template>
    </MainComponent>
</template>
<script lang="ts" setup>
    import {computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {LazyRecommendationsEditionList, LazyRecommendationsList, LazyRecommendationsExternalTopActions} from '#components';
    import {useAuth, useProfilePageViewMode, useTranslation} from '#imports';
    import {VJoyIconButton, VJoyWrapper} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {RecommendationRequestStartedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import type {Recommendation} from '~/types/domain/Recommendations';
    import RecommendationsCount from './old-ui/RecommendationsCount.vue';
    import {useRecommendationsStore} from './store/recommendations.store';

    const RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT = 2;

    // Stores
    const {loggedIn} = useAuth();
    const {profile} = storeToRefs(useProfileStore());
    const {recommendations, recommendationsAccepted, recommendationsAcceptedWithTextContent} = storeToRefs(useRecommendationsStore());

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled, isRecommendationEnabled, isTranslationEnabled} = useProfilePageViewMode();

    // Refs
    const recommendationSection = useTemplateRef('recommendationSection');
    const shouldDisplayAllRecommendations = ref<boolean>(false);

    // Computed values
    const title = computed((): string => t('profile.section.recommendation.title', {nbComment: recommendations.value?.recommendationsCount}));
    const isExternalActionsVisible = computed((): boolean => isRecommendationEnabled.value || isTranslationEnabled.value);
    const hasFilteredRecommendations = computed(() => recommendationsAccepted.value.length > 0);
    const displayedRecommendationsCount = computed(() => recommendationsAcceptedWithTextContent.value.length || 0);

    const displayedRecommendations = computed((): Recommendation[] => {
        if (loggedIn) {
            return shouldDisplayAllRecommendations.value || isEditionEnabled.value
                ? recommendationsAcceptedWithTextContent.value
                : recommendationsAcceptedWithTextContent.value.slice(0, RECOMMENDATIONS_TO_DISPLAY_BY_DEFAULT);
        } else {
            return [];
        }
    });

    function openEditRecommendation() {
        if (profile.value!.editMode === true) {
            eventBus.emit('recommendations:edit-mode', {entryPoint: RecommendationRequestStartedEntryPoint.SECTION_EDIT});
        }
    }

    function scrollToRecommendationsSection() {
        nextTick(() => {
            setTimeout(() => {
                recommendationSection.value?.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 600);
        });
    }

    function toggleShowMoreRecommendations(showAll: boolean): void {
        shouldDisplayAllRecommendations.value = showAll;
    }

    onMounted(() => {
        eventBus.on('main:recommendations:scroll-to', scrollToRecommendationsSection);

        // Component is ready
        eventBus.emit('main:recommendations:ready');
    });

    onUnmounted(() => {
        eventBus.off('main:recommendations:scroll-to', scrollToRecommendationsSection);
    });
</script>
<style lang="scss">
    @use '../../../node_modules/@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .recommendations {
        &__read-more {
            min-height: 15px;
        }
    }

    .recommendations-lock__message {
        flex-wrap: wrap;
        white-space: pre-wrap;
    }

    .add-reco {
        text-align: right;
        text-decoration: none;
        font-size: var(--joy-font-size-primary-400) !important;
        font-weight: var(--joy-font-weight-normal);
        color: var(--joy-color-secondary-50);
    }

    .edit-mode {
        cursor: pointer;
    }

    .recommendations-lock {
        padding: 12px;

        .opacity {
            opacity: 0.4;
            pointer-events: none;
        }
    }

    .recommendations-list {
        list-style-type: none;
        padding-left: 0;
        padding-right: 30px;

        @include mq.screen_xxs(max) {
            padding-left: unset;
        }

        &__item {
            &:not(:last-child) {
                border-bottom: 1px solid var(--joy-color-neutral-30);
            }
        }

        .recommendation {
            margin: var(--joy-core-spacing-4) 0;
        }
    }

    .recommendation-cta {
        max-width: 350px;

        :deep(*) {
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
</style>
